import { useState } from "react";
import axios from "axios";
import { Minus, Plus, X, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { authHeader } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartSheet = () => {
  const { items, updateQty, removeItem, total, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    track("checkout_start", { path: "/cart" });
    try {
      const res = await axios.post(`${API}/checkout`, {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      }, { headers: authHeader() });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col rounded-none" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight text-left">YOUR CART</SheetTitle>
          <SheetDescription className="text-left">Review your gear before checkout.</SheetDescription>
        </SheetHeader>
        {items.length === 0 ? (
          <div data-testid="cart-empty-message" className="flex-1 flex items-center justify-center text-muted-foreground text-sm uppercase tracking-widest">
            Cart is empty
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4 border border-border/60 p-3">
                  <div className="w-16 h-16 bg-secondary shrink-0 overflow-hidden">
                    {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{i.name}</p>
                    <p className="text-sm text-muted-foreground">${i.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => updateQty(i.id, i.quantity - 1)} className="border border-border w-6 h-6 flex items-center justify-center hover:bg-secondary transition-colors"><Minus size={12} /></button>
                      <span data-testid={`cart-qty-${i.id}`} className="text-sm w-6 text-center">{i.quantity}</span>
                      <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => updateQty(i.id, Math.min(i.quantity + 1, i.stock))} className="border border-border w-6 h-6 flex items-center justify-center hover:bg-secondary transition-colors"><Plus size={12} /></button>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${i.id}`} onClick={() => removeItem(i.id)} className="text-muted-foreground hover:text-primary transition-colors self-start"><X size={16} /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex justify-between font-display font-semibold text-lg">
                <span>Subtotal</span>
                <span data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Tax and shipping calculated at checkout.</p>
              <button
                data-testid="checkout-button"
                onClick={checkout}
                disabled={loading}
                className="w-full bg-primary text-white py-3 font-medium uppercase tracking-wider hover:bg-foreground transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Redirecting..." : "Checkout with Stripe"}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
